import { useEffect } from "react";
import { skipToken } from "@tanstack/react-query";
import { FaXmark } from "react-icons/fa6";
import type { MessagesId } from "cadendar-shared";

import { trpc } from "../../main/components/trpc.ts";
import { useSelectedPatientIds } from "../../main/state/globalState";
import RespondMessageCard from "../../respond/components/RespondMessageCard";
import {Button} from "@/components/ui/button";

interface RespondReplayModalProps {
  isOpen: boolean;
  ownerId: string;
  messageId: MessagesId | null;
  onClose: () => void;
}

export default function RespondReplayModal(props: RespondReplayModalProps) {
  const [_selectedPatientIds, { selectPatientId }] = useSelectedPatientIds();
  const replayQuery = trpc.message.getRespondReplayForMessage.useQuery(
    props.isOpen && props.messageId
      ? { messageId: props.messageId }
      : skipToken,
  );
  const replayPatientId = replayQuery.data?.patientId;

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        props.onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [props.isOpen, props.onClose]);

  if (!props.isOpen) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="respond-replay-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
      onClick={props.onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-slate-100 p-4 shadow-[0_24px_80px_rgba(15,23,42,0.35)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h2
              id="respond-replay-modal-title"
              className="m-0 text-xl font-semibold text-slate-950"
            >
              Message vocal Respond
            </h2>
            <div className="text-sm text-slate-600">
              Réécoute du message vocal depuis l&apos;historique Cadendar.
            </div>
          </div>

          <Button
            type="button"
            size="icon-sm"
            variant="secondary"
            className="rounded-full"
            onClick={props.onClose}
            aria-label="Fermer"
            title="Fermer"
          >
            <FaXmark />
          </Button>
        </div>

        {replayQuery.isLoading ? (
          <div className="rounded-2xl bg-white px-5 py-8 text-center text-slate-600">
            Chargement du message vocal Respond...
          </div>
        ) : replayQuery.data ? (
          <RespondMessageCard
            ownerId={props.ownerId}
            message={replayQuery.data}
            isDeleting={false}
            onDelete={() => undefined}
            showDeleteAction={false}
            onPatientNameClick={
              replayPatientId
                ? () => {
                    selectPatientId(replayPatientId);
                  }
                : undefined
            }
          />
        ) : (
          <div className="rounded-2xl bg-white px-5 py-8 text-center text-slate-600">
            Aucun message vocal Respond n&apos;a pu être relié à ce message.
          </div>
        )}
      </div>
    </div>
  );
}